import React, { useState } from 'react';
import { Paper, Box, Stack, Typography, IconButton, Button, Skeleton, type SxProps, type Theme } from '@mui/material';
import { MoreHorizontal, Pencil, Trash2, Bell } from 'lucide-react';
import Thumbnail from 'components/atomic/atoms/thumbnail';
import { SavedSearchThumbnailPlaceholder } from './saved-search-thumbnail-placeholder';
import PillBadge from 'components/atomic/organisms/pill-badge';
import PillSelect, { type PillSelectOption } from 'components/atomic/molecules/pill-select';
import RoundedMenu, { RoundedMenuItem } from 'components/atomic/molecules/rounded-menu';
import InlineEditField from 'components/atomic/molecules/inline-edit-field';

const CONTROL_HEIGHT = '1.5rem';
const SERIF = 'var(--themes-fonts-heading), Georgia, serif';
/** Body font for everything that is not a heading. */
const BODY = 'var(--themes-fonts-text), sans-serif';
/** Shared card shell so the real card and its loading skeleton keep identical dimensions. */
const SHELL_SX: SxProps<Theme> = { p: 1.5, borderRadius: '16px', border: '2px solid transparent', backgroundColor: '#f9fafb' };

/** Pre-translated labels the card renders (kept out of the domain component so it stays i18n-free). */
export interface SavedSearchCardLabels {
  /** aria-label for the kebab menu button. */
  options: string;
  rename: string;
  delete: string;
  /** aria-label for the alert-frequency dropdown. */
  frequency: string;
  /** aria-label for the rename confirm button. */
  confirmRename: string;
  /** Label for the unselect button (shown when the card is selected). */
  unselect: string;
}

/** Content props for a populated card (everything except the `loading` flag). */
export interface SavedSearchCardContentProps {
  /** Saved search name (already resolved for display). */
  title: string;
  /** Pre-formatted filter summary line, e.g. "Annex · House · 2+ bd". */
  summary: string;
  /** Pre-translated UI labels. */
  labels: SavedSearchCardLabels;
  /** Thumbnail image; falls back to a bookmark placeholder when empty/broken. */
  imageUrl?: string | null;
  /** New-listings badge label, e.g. "8 New". Omit to hide the badge. */
  newListingsLabel?: string;
  /** Currently-selected alert-frequency option value. */
  frequencyValue: string;
  /** Alert-frequency options for the dropdown. */
  frequencyOptions: PillSelectOption<string>[];
  onFrequencyChange: (value: string) => void;
  /** Commit a rename (already trimmed & non-empty). */
  onRename: (name: string) => void;
  onDelete: () => void;
  /** Whether this card is the currently-selected one. */
  selected?: boolean;
  /** Called when the card is clicked to select it. */
  onSelect?: () => void;
  /** Called when the Unselect button is pressed. */
  onUnselect?: () => void;
}

/**
 * Either a populated card, or a loading skeleton (`loading: true`) that needs no
 * content — same shell/dimensions either way.
 */
export type SavedSearchCardProps =
  | (SavedSearchCardContentProps & { loading?: false })
  | { loading: true };

/**
 * SavedSearchCard
 *
 * Domain component (Layer 2, `src/ui/`). Renders one saved search: thumbnail,
 * name (with inline rename), filter summary, a new-listings badge, an alert
 * frequency dropdown, and a kebab menu (Rename / Delete). Receives only
 * pre-formatted strings — no `domain/` imports, no i18n. Owns only its local UI
 * state (menu anchor + rename editing) and calls back for every mutation.
 */
export function SavedSearchCard(props: SavedSearchCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  // Loading skeleton: the same shell with grey pulsing boxes in place of the
  // thumbnail, name, summary, and control row. (`'title' in props` narrows the union
  // to the loading variant, which carries no content.)
  if (!('title' in props)) {
    return (
      <Paper
        elevation={0}
        data-testid="SavedSearchCardSkeleton"
        sx={{ ...SHELL_SX, '& .MuiSkeleton-root': { animationDuration: '1.1s' } }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Skeleton animation="pulse" variant="rounded" width="4rem" height="4rem" sx={{ borderRadius: '20px', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton animation="pulse" variant="rounded" width="55%" height={16} sx={{ borderRadius: '6px' }} />
            <Skeleton animation="pulse" variant="rounded" width="80%" height={12} sx={{ borderRadius: '6px', mt: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, mt: '0.75rem' }}>
              <Skeleton animation="pulse" variant="rounded" width={44} height={CONTROL_HEIGHT} sx={{ borderRadius: '100vh' }} />
              <Skeleton animation="pulse" variant="rounded" width={88} height={CONTROL_HEIGHT} sx={{ borderRadius: '100vh' }} />
            </Box>
          </Box>
        </Stack>
      </Paper>
    );
  }

  const {
    title,
    summary,
    labels,
    imageUrl,
    newListingsLabel,
    frequencyValue,
    frequencyOptions,
    onFrequencyChange,
    onRename,
    onDelete,
    selected = false,
    onSelect,
    onUnselect,
  } = props;

  const closeMenu = () => setMenuAnchor(null);

  const startRename = () => {
    setRenameValue(title);
    setIsRenaming(true);
    closeMenu();
  };
  const confirmRename = () => {
    const name = renameValue.trim();
    if (name) {
      onRename(name);
    }
    setIsRenaming(false);
  };
  const cancelRename = () => setIsRenaming(false);

  const handleDelete = () => {
    onDelete();
    closeMenu();
  };

  return (
    <Paper
      elevation={0}
      onClick={() => (selected ? onUnselect?.() : onSelect?.())}
      sx={{
        ...SHELL_SX,
        border: selected ? '2px solid #6b7280' : '2px solid transparent',
        backgroundColor: selected ? '#fff' : '#f9fafb',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        '&:hover': { backgroundColor: selected ? '#fff' : '#f1f2f4' },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Thumbnail
          src={imageUrl}
          size="4rem"
          radius="20px"
          fallback={<SavedSearchThumbnailPlaceholder />}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Upper: name + summary, with the options button top-right */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isRenaming ? (
                <Box onClick={event => event.stopPropagation()}>
                  <InlineEditField
                    value={renameValue}
                    onChange={setRenameValue}
                    onConfirm={confirmRename}
                    onCancel={cancelRename}
                    confirmLabel={labels.confirmRename}
                  />
                </Box>
              ) : (
                <Typography sx={{ fontFamily: SERIF, fontSize: '1rem', fontWeight: 400, color: '#0F1729' }} noWrap>
                  {title}
                </Typography>
              )}
              <Typography sx={{ fontFamily: BODY, fontSize: 12, color: '#9ca3af' }} noWrap>
                {summary}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={labels.options}
              disableRipple
              onClick={event => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
              sx={{ flexShrink: 0, mt: '-4px', mr: '-4px', p: '8px', '&:hover': { backgroundColor: '#fff' }}}
            >
              <MoreHorizontal size={16} />
            </IconButton>
          </Stack>

          {/* Bottom: new-count chip + alert-frequency dropdown (+ Unselect when selected).
              Clicking the row (whitespace or the badge) toggles selection; only the
              interactive controls below stop propagation so they don't also toggle it. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '0.5rem' }}>
            {newListingsLabel && (
              <PillBadge
                backgroundColor="#438ab6"
                textColor="#fff"
                sx={{
                  flexShrink: 0,
                  height: CONTROL_HEIGHT,
                  borderRadius: '100vh',
                  fontFamily: BODY,
                  fontSize: '0.625rem',
                  fontWeight: 300,
                  padding: '0 10px',
                }}
              >
                {newListingsLabel}
              </PillBadge>
            )}
            {/* The frequency control (and its dropdown) must not toggle card selection. */}
            <Box sx={{ display: 'inline-flex' }} onClick={event => event.stopPropagation()}>
              <PillSelect
                ariaLabel={labels.frequency}
                value={frequencyValue}
                onChange={onFrequencyChange}
                height={CONTROL_HEIGHT}
                leadingIcon={<Bell size={12} color="#438ab6" />}
                options={frequencyOptions}
                sx={{ fontFamily: BODY }}
              />
            </Box>
            {selected && (
              <Button
                type="button"
                disableRipple
                onClick={event => {
                  event.stopPropagation();
                  onUnselect?.();
                }}
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  minWidth: 0,
                  height: CONTROL_HEIGHT,
                  px: 1,
                  py: 0,
                  borderRadius: '100vh',
                  backgroundColor: '#f9fafb',
                  color: '#6b7280',
                  fontFamily: BODY,
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  lineHeight: 1,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#f1f2f4' },
                }}
              >
                {labels.unselect}
              </Button>
            )}
          </Box>
        </Box>
      </Stack>

      <RoundedMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        // The menu is a React child of the card, so (via portal event bubbling) a
        // click on a menu item would otherwise reach the card's select handler.
        onClick={event => event.stopPropagation()}
        disableRestoreFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <RoundedMenuItem onClick={startRename}>
          <Pencil size={14} style={{ marginRight: 8 }} />
          {labels.rename}
        </RoundedMenuItem>
        <RoundedMenuItem onClick={handleDelete} sx={{ color: '#dc2626', '&:hover': { backgroundColor: '#fef2f2' }}}>
          <Trash2 size={14} style={{ marginRight: 8 }} />
          {labels.delete}
        </RoundedMenuItem>
      </RoundedMenu>
    </Paper>
  );
}

export default SavedSearchCard;
